import { chatEscalationApi, usersManagementApi } from 'app/services/api';

import { DeletedUsersPolicy, USER_DISPLAY_NAME_FIELDS } from 'constants/userSearch';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';
import { getUserDisplayName } from 'utils/user';

import { selectMasterCompanyId } from 'store/auth/selectors';

export const CHAT_ESCALATION_ACTION_TYPE = createNamedActions('chatEscalation', {
  GET_LADDER: 'GET_LADDER',
  SAVE_LADDER: 'SAVE_LADDER',
  DELETE_LADDER: 'DELETE_LADDER',
  RESET_STATE: 'RESET_STATE',
});

// PUT/DELETE echo "the read shape"; be resilient to either an array (matching GET) or
// a bare single group in case the backend returns the replaced group unwrapped.
const toGroupsArray = data => {
  if (Array.isArray(data)) {
    return data;
  }

  return data ? [data] : [];
};

const collectResolvedUserIds = groups => {
  const ids = (groups ?? []).flatMap(group =>
    (group.levels ?? []).flatMap(level =>
      (level.targets ?? []).flatMap(target => target.resolvedUserIds ?? []),
    ),
  );

  return [...new Set(ids)];
};

// The ladder's targets carry only resolvedUserIds; enrich each target with the
// resolved display names in one batch call so the UI never has to resolve per-chip.
const withResolvedUserNames = async (groups, companyId) => {
  const ids = collectResolvedUserIds(groups);

  if (!ids.length || !companyId) {
    return groups;
  }

  const namesById = await usersManagementApi
    .searchUsersByIds({
      companyId,
      ids,
      fields: USER_DISPLAY_NAME_FIELDS,
      deletedPolicy: DeletedUsersPolicy.INCLUDE_DELETED,
    })
    .then(users =>
      users.reduce((acc, user) => {
        acc[user.id] = getUserDisplayName(user);

        return acc;
      }, {}),
    )
    .catch(() => ({}));

  return groups.map(group => ({
    ...group,
    levels: (group.levels ?? []).map(level => ({
      ...level,
      targets: (level.targets ?? []).map(target => ({
        ...target,
        resolvedUserNames: (target.resolvedUserIds ?? []).map(id => namesById[id]).filter(Boolean),
      })),
    })),
  }));
};

// Company-global ladder (DEV-5500): reads the caller's own company id from state — the
// ladder is no longer scoped to a subCompanyId.
export const getChatEscalationLadderAction = catchAsyncActionError(
  () => async (dispatch, getState) => {
    const companyId = selectMasterCompanyId(getState());
    const { data } = await chatEscalationApi.getLadder({ companyId });
    const payload = await withResolvedUserNames(toGroupsArray(data), companyId);

    return dispatch({
      type: CHAT_ESCALATION_ACTION_TYPE.GET_LADDER,
      payload,
    });
  },
);

export const saveChatEscalationLadderAction = catchAsyncActionError(
  ({ group }) =>
    async (dispatch, getState) => {
      const companyId = selectMasterCompanyId(getState());
      const { data } = await chatEscalationApi.updateLadder({ companyId, groups: [group] });
      const payload = await withResolvedUserNames(toGroupsArray(data), companyId);

      return dispatch({
        type: CHAT_ESCALATION_ACTION_TYPE.SAVE_LADDER,
        payload,
      });
    },
);

export const deleteChatEscalationLadderAction = catchAsyncActionError(
  () => async (dispatch, getState) => {
    const companyId = selectMasterCompanyId(getState());

    await chatEscalationApi.deleteLadder({ companyId });

    return dispatch({ type: CHAT_ESCALATION_ACTION_TYPE.DELETE_LADDER });
  },
);
